import { Link, useNavigate } from 'react-router-dom';
import { useTrail } from './TrailProvider';
import { useFirebase } from './FirebaseContextProvider';
import { useEntities } from '../utils/hooks';
import { getBaseRoute } from './LoginCard';

const MAX_DISPLAY = 7; // how many items to show after Home

// Which registry answers a breadcrumb. Referees are users like everyone else —
// `refereesById` only knows the ones assigned to a game in a loaded tournament
const kindBySegment = {
  tournaments: 'tournaments',
  teams: 'teams',
  referees: 'users',
  'referee-assigners': 'users',
  players: 'users',
  coaches: 'users',
  'field-managers': 'users',
  parents: 'users',
  none: 'users',
};

const trailIdsByKind = (records, wanted) => {
  const ids = new Set();
  for (const { url } of records) {
    const [seg, id] = url.split('/').filter(Boolean);
    if (id && kindBySegment[seg] === wanted) ids.add(id);
  }
  return [...ids];
};

// Label from path (last segment)
const labelFromPath = (path) => {
  const parts = path.split('/').filter(Boolean);
  return decodeURIComponent(parts[parts.length - 1] || '/');
};

export default function TrailBar() {
  const { trail } = useTrail(); // full visible up to currentIdx
  const { user, usersById, tournamentsById, teamsById } = useFirebase();
  const navigate = useNavigate();

  // Root path selection
  const baseRoute = getBaseRoute(user);

  // Full records (url+idx) from trail
  const currentIdx = trail.length ? trail[trail.length - 1].idx : 0; // last is current
  const homePaths = new Set([baseRoute, '/']);
  const hasRootInTrail = trail.length > 0 && trail[0].url === baseRoute; // root present as first

  // Drop all home/root records (we render Home separately)
  const contentRecords = trail.filter((rec) => !homePaths.has(rec.url));

  // Compute overflow and slice the tail
  const overflow = contentRecords.length > MAX_DISPLAY;
  const visibleTail = overflow
    ? contentRecords.slice(-MAX_DISPLAY)
    : contentRecords;

  // Only the crumbs on screen: the trail itself is unbounded and lives for the
  // whole session, and the pages behind it are unmounted — so the bar holds a
  // registration of its own rather than trusting the registry's cold cache.
  useEntities('users', trailIdsByKind(visibleTail, 'users'));
  useEntities('teams', trailIdsByKind(visibleTail, 'teams'));
  useEntities('tournaments', trailIdsByKind(visibleTail, 'tournaments'));

  // Resolve human label by route segment
  const resolveLabel = (path) => {
    const parts = path.split('/').filter(Boolean);
    if (parts.length < 2) return labelFromPath(path);
    const [seg, id] = parts;
    if (!id) return labelFromPath(path);

    let rec = null;
    switch (seg) {
      case 'tournaments':
        rec = tournamentsById.get(id);
        break;
      case 'teams':
        rec = teamsById.get(id);
        break;
      case 'referees':
      case 'referee-assigners':
      case 'players':
      case 'coaches':
      case 'field-managers':
      case 'parents':
      case 'none':
        rec = usersById.get(id);
        break;
      default:
        rec = null;
    }
    return rec?.name || labelFromPath(path);
  };

  // Nothing to show beyond Home (we still render Home link below)
  return (
    <nav className='trailbar'>
      {/* Home link is always first */}
      <Link
        to={baseRoute}
        className='trail-link fa-home'
        aria-label='Home'
        onClick={(e) => {
          // If root exists in trail, go back N steps; else replace to root
          if (hasRootInTrail) {
            e.preventDefault();
            const delta = currentIdx - trail[0].idx;
            if (delta > 0) navigate(-delta);
          } else {
            e.preventDefault();
            navigate(baseRoute, { replace: true });
          }
        }}
      />

      {/* Ellipsis if we hid older segments */}
      {overflow && <span className='trail-sep'> / </span>}
      {overflow && <span className='trail-ellipsis'>…</span>}

      {/* Render visible tail as links with separators */}
      {visibleTail.map((rec, idx) => (
        <span key={`${rec.url}-${idx}`} className='trail-item'>
          <span className='trail-sep'> / </span>
          <Link
            to={rec.url}
            className='trail-link'
            onClick={(e) => {
              // Go back exactly to this record's idx
              const delta = currentIdx - rec.idx;
              if (delta > 0) {
                e.preventDefault();
                navigate(-delta);
              } else {
                // delta === 0 means current node; do nothing to avoid push
                e.preventDefault();
              }
            }}
          >
            {resolveLabel(rec.url)}
          </Link>
        </span>
      ))}
    </nav>
  );
}
